import React from "react";
import { DownloadIcon } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Invoice, InvoiceLine } from "@/model/interface/Invoice";
import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_PILLS,
  formatInvoiceDate,
  formatInvoiceMoney,
  formatInvoicePeriod,
  formatInvoiceQuantity,
  invoiceDocumentName,
} from "@/util/invoice";

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">{children}</p>;
}

function TotalRow({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-4 py-1 text-sm ${strong ? "font-semibold" : ""}`}>
      <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function LineRow({ line }: { line: InvoiceLine }) {
  const summary = [formatInvoiceQuantity(line.quantity, line.unit), line.taxCategory].filter(Boolean).join(" · ");

  return (
    <div className="flex items-start justify-between gap-4 border-b py-2.5 text-sm last:border-b-0">
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium">{line.description}</p>
        <p className="text-xs text-muted-foreground">{summary}</p>
        {line.detail && <p className="text-xs text-muted-foreground">{line.detail}</p>}
        {line.discount > 0 && (
          <p className="text-xs text-muted-foreground">{line.discountDescription ?? "Discount"}</p>
        )}
      </div>
      <div className="shrink-0 text-right tabular-nums">
        <p className="font-medium">{formatInvoiceMoney(line.amount + line.discount)}</p>
        {line.discount > 0 && (
          <p className="text-xs text-muted-foreground">{formatInvoiceMoney(-line.discount)}</p>
        )}
        <p className="text-xs text-muted-foreground">Tax {formatInvoiceMoney(line.tax)}</p>
      </div>
    </div>
  );
}

interface InvoiceDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice?: Invoice;
  isLoading: boolean;
  isDownloading: boolean;
  onDownload: (invoiceId: string) => void;
}

export default function InvoiceDetailsSheet({
  open,
  onOpenChange,
  invoice,
  isLoading,
  isDownloading,
  onDownload,
}: InvoiceDetailsSheetProps) {
  const lines = invoice?.lines ?? [];
  const planLines = lines.filter((line) => line.section === "PLAN");
  const usageLines = lines.filter((line) => line.section === "USAGE");
  const balanceLines = lines.filter((line) => line.section === "BALANCE");
  const refundLines = lines.filter((line) => line.section === "REFUND");
  const isCreditNote = invoice?.documentType === "CREDIT_NOTE";
  const discountTotal = lines.reduce((total, line) => total + (line.discount ?? 0), 0);
  const jurisdictions = invoice?.taxJurisdictions ?? [];
  const refunds = invoice?.refunds ?? [];
  const party = invoice?.billedTo;
  const partyAddress = party
    ? [party.addressLine1, party.city, [party.state, party.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ")
    : "";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto text-foreground sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{invoice ? invoiceDocumentName(invoice) : "Invoice"}</SheetTitle>
          <SheetDescription>{invoice?.issuedAt ? `Issued ${formatInvoiceDate(invoice.issuedAt)}` : ""}</SheetDescription>
        </SheetHeader>

        {isLoading || !invoice ? (
          <div className="space-y-3 p-4">
            {[1, 2, 3, 4].map((row) => (
              <Skeleton key={row} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-6 p-4">
            <div className="flex items-center justify-between gap-4">
              <span className={INVOICE_STATUS_PILLS[invoice.status]}>{INVOICE_STATUS_LABELS[invoice.status]}</span>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={isDownloading}
                onClick={() => onDownload(invoice.id)}
              >
                <DownloadIcon className="h-4 w-4" />
                Download PDF
              </Button>
            </div>

            {party && (
              <div>
                <SectionLabel>Billed to</SectionLabel>
                <p className="text-sm font-medium">{party.name}</p>
                <p className="text-sm text-muted-foreground">{partyAddress}</p>
              </div>
            )}

            {planLines.length > 0 && (
              <div>
                <SectionLabel>Plan and add-ons · {formatInvoicePeriod(invoice.planPeriodStart, invoice.planPeriodEnd)}</SectionLabel>
                {planLines.map((line) => (
                  <LineRow key={line.id} line={line} />
                ))}
              </div>
            )}

            {usageLines.length > 0 && (
              <div>
                <SectionLabel>Usage · {formatInvoicePeriod(invoice.usagePeriodStart, invoice.usagePeriodEnd)}</SectionLabel>
                {usageLines.map((line) => (
                  <LineRow key={line.id} line={line} />
                ))}
              </div>
            )}

            {balanceLines.length > 0 && (
              <div>
                <SectionLabel>Prepaid balance</SectionLabel>
                {balanceLines.map((line) => (
                  <LineRow key={line.id} line={line} />
                ))}
              </div>
            )}

            {refundLines.length > 0 && (
              <div>
                <SectionLabel>{invoice.relatedNumber ? `Refund · for ${invoice.relatedNumber}` : "Refund"}</SectionLabel>
                {refundLines.map((line) => (
                  <LineRow key={line.id} line={line} />
                ))}
              </div>
            )}

            {jurisdictions.length > 0 && (
              <div>
                <SectionLabel>Tax by jurisdiction</SectionLabel>
                {jurisdictions.map((jurisdiction, index) => (
                  <TotalRow
                    key={`${jurisdiction.jurisdiction}-${jurisdiction.level}-${index}`}
                    label={[jurisdiction.jurisdiction, jurisdiction.ratePercent ? `${jurisdiction.ratePercent}%` : null]
                      .filter(Boolean)
                      .join(", ")}
                    value={formatInvoiceMoney(jurisdiction.amount)}
                  />
                ))}
              </div>
            )}

            <div className="border-t pt-3">
              {discountTotal > 0 && (
                <>
                  <TotalRow label="List price" value={formatInvoiceMoney(invoice.subtotal + discountTotal)} />
                  <TotalRow label="Discounts" value={formatInvoiceMoney(-discountTotal)} />
                </>
              )}
              <TotalRow label="Subtotal" value={formatInvoiceMoney(invoice.subtotal)} />
              <TotalRow label="Sales tax" value={formatInvoiceMoney(invoice.taxTotal)} />
              <TotalRow label="Total" value={formatInvoiceMoney(invoice.total)} strong />
              {invoice.paidByCard > 0 && (
                <TotalRow
                  label={isCreditNote ? `Refunded to ${invoice.cardLabel ?? "card"}` : (invoice.cardLabel ?? "Card")}
                  value={formatInvoiceMoney(-invoice.paidByCard)}
                />
              )}
              {invoice.paidFromBalance > 0 && (
                <TotalRow label="Prepaid balance" value={formatInvoiceMoney(-invoice.paidFromBalance)} />
              )}
              {refunds.map((refund, index) => (
                <TotalRow
                  key={`${refund.refundedAt ?? "refund"}-${index}`}
                  label={[
                    refund.refundedAt ? `Refunded ${formatInvoiceDate(refund.refundedAt)}` : "Refunded",
                    refund.creditNoteNumber,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  value={formatInvoiceMoney(refund.amount)}
                />
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
